import { createContext, useContext, useEffect, useMemo } from "react";
import useThemeStore from "@/store/themeStore";

const ThemeContext = createContext(null);

/**
 * Apply theme CSS variables to document root
 */
const applyThemeToDocument = (theme) => {
  if (!theme) return;
  
  const root = document.documentElement;
  
  // Apply color variables - check if colors exists and is an object
  if (theme.colors && typeof theme.colors === 'object') {
    try {
      Object.entries(theme.colors).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--${key}`, value);
        }
      });
    } catch (error) {
      console.error('[ThemeProvider] Error applying colors:', error);
    }
  }
  
  // Apply gradient CSS custom properties
  if (theme.gradients && typeof theme.gradients === 'object') {
    if (theme.gradients.body) {
      root.style.setProperty('--gradient-body', theme.gradients.body);
    }
    if (theme.gradients.sidebar) {
      root.style.setProperty('--gradient-sidebar', theme.gradients.sidebar);
    }
  }
  
  // Set data-theme attribute
  if (theme.id) {
    root.setAttribute('data-theme', theme.id);
  }
};

export function ThemeProvider({ children }) {
  const activeTheme = useThemeStore((state) => state.activeTheme);
  const getActiveTheme = useThemeStore((state) => state.getActiveTheme);
  const fetchActiveTheme = useThemeStore((state) => state.fetchActiveTheme);
  const isLoading = useThemeStore((state) => state.isLoading);
  
  // Admin-only functions
  const themes = useThemeStore((state) => state.themes);
  const fetchAllThemes = useThemeStore((state) => state.fetchAllThemes);
  const activateTheme = useThemeStore((state) => state.activateTheme);
  const createTheme = useThemeStore((state) => state.createTheme);
  const updateTheme = useThemeStore((state) => state.updateTheme);
  const deleteTheme = useThemeStore((state) => state.deleteTheme);
  const applyPreviewTheme = useThemeStore((state) => state.applyPreviewTheme);
  const revertToActiveTheme = useThemeStore((state) => state.revertToActiveTheme);

  // Fetch active theme on mount
  useEffect(() => {
    fetchActiveTheme();
  }, [fetchActiveTheme]);

  // Apply theme when activeTheme changes
  useEffect(() => {
    // Ensure we have a valid theme before applying
    if (activeTheme && (activeTheme.colors || activeTheme.id)) {
      applyThemeToDocument(activeTheme);
    } else {
      // Fallback to default theme if activeTheme is invalid
      console.warn('[ThemeProvider] Invalid activeTheme, using default');
      const defaultTheme = useThemeStore.getState().getActiveTheme();
      if (defaultTheme) {
        applyThemeToDocument(defaultTheme);
      }
    }
  }, [activeTheme]);

  // Memoized context value
  const value = useMemo(() => ({
    // For all users
    theme: getActiveTheme(),
    isLoading,
    
    // Admin-only
    themes,
    fetchAllThemes,
    activateTheme,
    createTheme,
    updateTheme,
    deleteTheme,
    applyPreviewTheme,
    revertToActiveTheme,
  }), [
    getActiveTheme,
    isLoading,
    themes,
    fetchAllThemes,
    activateTheme,
    createTheme,
    updateTheme,
    deleteTheme,
    applyPreviewTheme,
    revertToActiveTheme,
  ]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
